import React, { ReactElement, useState } from 'react';
import moment from 'moment';
import {
  UserSubscriptionStripeConnectStatus,
  RevenueShare,
  TAX_LABEL,
  UserSubscriptionPlanPaymentTransaction,
  UserSubscriptionPlanPaymentTransactions,
  UserSubscriptionPlanPaymentTransactionStatus,
} from '../../../../ReduxStorage/UserSubscriptionPlans/constants';
import { AlertMessage } from '../../../components/AlertMessage/component';
import { Chip } from '../../../components/Chip/component';
import { Select } from '../../../components/Select/component';
import {
  getChipVariantFromStripeConnectStatus,
  getChipVariantFromPaymentTransactionStatus,
  makeCountryOptions,
} from './helpers';
import { StripeConnectCountries } from './constants';
import { capitalizeFirstLetter } from '../../../utilities/utilities';
import { UserIcon, UserRow } from '../ManageUsers/styles';
import {
  ActionsContainer,
  BannerContainer,
  BannerLinkStyle,
  Header,
  Heading,
  InputContainer,
  NeedHelpLinkStyle,
  SelectErrorContainer,
  StyledButton,
  StyledTable,
  StyledModalTable,
  ViewDetailsButton,
} from './styles';
import { ActionButton } from '../../../components/Buttons/ActionButton/ActionButton';
import {
  ROUTE_CONTACT_PAGE,
  ROUTE_COURSE_PAGE,
  ROUTE_USER_PROFILE_PAGE,
} from '../../../../ReduxStorage/routeActions';
import { RouteLink } from '../../../components/RouteLink/component';
import { SubscriptionPlanType } from '../../../../ReduxStorage/SubscriptionPlans/constants';
import { ModalDialog } from '../../../components/Modals/ModalDialog';
import { Paginate } from '../../../components/Pagination/Paginate';
import { CursorPageSize } from '../../../components/Pagination/types';

export type CourseMarketPlaceSetupProps = {
  planType: SubscriptionPlanType;
  accountStatus: UserSubscriptionStripeConnectStatus;
  onSetupStripeAccountClick: (country: string) => Promise<void>;
};

const MarketPlaceDisabledAlert = (): ReactElement => (
  <AlertMessage
    kind="info"
    title="Revenue sharing is off"
    message={
      <span>
        Please reach out to{' '}
        <BannerLinkStyle href="mailto:support@openlearning.com">
          support@openlearning.com
        </BannerLinkStyle>{' '}
        to discuss enabling revenue sharing for your Marketplace courses.
      </span>
    }
  />
);

const NoStripeAccountAlert = (): ReactElement => (
  <AlertMessage
    kind="info"
    title="Set up your Stripe Connect account"
    message="To get paid for your courses that are listed on the marketplace for both learner enrolment and paid certification, please create a Stripe connect account."
  />
);

const COUNTRY_OPTIONS = makeCountryOptions();

const CourseMarketplaceSetup = ({
  accountStatus,
  onSetupStripeAccountClick,
}: CourseMarketPlaceSetupProps): ReactElement => {
  const [country, setCountry] = useState('');
  const disabled = accountStatus === 'no_revenue_share';
  return (
    <div>
      <BannerContainer>
        {disabled ? <MarketPlaceDisabledAlert /> : <NoStripeAccountAlert />}
      </BannerContainer>
      <InputContainer>
        <Select
          label="Country"
          description="Requires you to have a bank account in this country"
          value={country}
          options={COUNTRY_OPTIONS}
          onValueChange={setCountry}
          required
          disabled={disabled}
        />
        {accountStatus === 'no_stripe_account' &&
          country !== '' &&
          !StripeConnectCountries.includes(country) && (
            <SelectErrorContainer>
              Unfortunately Stripe Connect is currently available in your
              country and the payout process will be done manually. To review
              your course marketplace revenue sharing and payment terms, please{' '}
              <a href="mailto:support@openlearning.com">contact us</a>.
            </SelectErrorContainer>
          )}
      </InputContainer>
      <ActionsContainer>
        <ActionButton
          disabled={disabled || !StripeConnectCountries.includes(country)}
          onClick={() => onSetupStripeAccountClick(country)}
          colour="kelp"
          label="Set up your stripe account"
        />
      </ActionsContainer>
      <div>
        <span>
          Have a question about how Stripe Connect works?{' '}
          <NeedHelpLinkStyle href="mailto:support@openlearning.com">
            Contact us
          </NeedHelpLinkStyle>
        </span>
      </div>
    </div>
  );
};

export type CourseMarketplaceManagerProps = {
  subscriptionId: string;
  accountStatus: UserSubscriptionStripeConnectStatus;
  paymentTransactions: UserSubscriptionPlanPaymentTransactions;
  goToStripeConnect: () => Promise<void>; // the link
  fetchSubscriptionPaymentTransactions: (
    subscriptionId: string,
    afterPaymentId?: string,
    limit?: number,
    page?: number
  ) => Promise<void>;
  revenueShare: RevenueShare;
};

const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 10;

const CourseMarketplaceManager = ({
  subscriptionId,
  accountStatus,
  paymentTransactions,
  goToStripeConnect,
  fetchSubscriptionPaymentTransactions,
  revenueShare,
}: CourseMarketplaceManagerProps): ReactElement => {
  const [showViewDetailsModal, setShowViewDetailsModal] = useState(false);
  const [
    transactionDetails,
    setTransactionDetails,
  ] = useState<UserSubscriptionPlanPaymentTransaction | null>(null);
  const [paymentsPageNumber, setPaymentsPageNumber] = useState(
    DEFAULT_PAGE_NUMBER
  );
  const [paymentsPageSize, setPaymentsPageSize] = useState(DEFAULT_PAGE_SIZE);

  const onHideModal = (): void => {
    setShowViewDetailsModal(false);
    setTransactionDetails(null);
  };

  const onViewDetailsAction = (
    transaction: UserSubscriptionPlanPaymentTransaction
  ): void => {
    setShowViewDetailsModal(true);
    setTransactionDetails(transaction);
  };

  const handlePageSizeChange = (pageSize: CursorPageSize): void => {
    fetchSubscriptionPaymentTransactions(
      subscriptionId,
      undefined,
      Number(pageSize),
      1
    );
    setPaymentsPageSize(pageSize);
    setPaymentsPageNumber(1);
  };

  const handlePageChange = (newPage: number): void => {
    if (!paymentTransactions.results[newPage]) {
      const currentPaymentsPageList =
        paymentTransactions.results[paymentsPageNumber];
      const afterPaymentId =
        currentPaymentsPageList[currentPaymentsPageList.length - 1].id;

      fetchSubscriptionPaymentTransactions(
        subscriptionId,
        afterPaymentId,
        Number(paymentsPageSize),
        newPage
      );
    }

    setPaymentsPageNumber(newPage);
  };

  const { total, results: paymentTransactionsPaginated } = paymentTransactions;

  return (
    <div>
      <StyledTable>
        <tr>
          {!revenueShare.useOlConnect && <th>Stripe Connect Account</th>}
          <th>Processing fee</th>
          <th>Taxation</th>
          <th>Payout cycle</th>
          <th>Actions</th>
        </tr>
        <tr>
          {!revenueShare.useOlConnect && (
            <td>
              <Chip
                variant={getChipVariantFromStripeConnectStatus(accountStatus)}
                label={capitalizeFirstLetter(accountStatus)}
              />
            </td>
          )}
          <td>
            {revenueShare.processingFees
              ? 'Yes - deducted automatically '
              : 'No - absorbed by OpenLearning'}
          </td>
          <td>{TAX_LABEL[revenueShare.tax]}</td>
          <td>Weekly</td>
          <td>
            {revenueShare.useOlConnect ? (
              <RouteLink
                to={{
                  type: ROUTE_CONTACT_PAGE,
                }}
              >
                Contact us
              </RouteLink>
            ) : (
              <StyledButton
                variant="legacy-kelp"
                action={{
                  kind: 'on_click',
                  value: () => goToStripeConnect(),
                }}
              >
                Go to Stripe Connect
              </StyledButton>
            )}
          </td>
        </tr>
      </StyledTable>
      <div>
        <Heading className="below-this" id="below-this">
          Payment transaction history
        </Heading>
        <StyledTable>
          <thead>
            <tr>
              <th>Item</th>
              <th>Learner</th>
              <th>Amount you receive {/* Add info tip here */}</th>
              <th>Processing fees {/* Add info tip here */}</th>
              <th>Revenue share</th>
              <th>Date</th>
              <th>Status</th>
              <th>Payout receipt</th>
            </tr>
          </thead>
          <tbody>
            {(paymentTransactionsPaginated[paymentsPageNumber] || []).map(
              (transaction) => (
                <tr>
                  <td>
                    <RouteLink
                      to={{
                        type: ROUTE_COURSE_PAGE,
                        params: {
                          courseCode: transaction.coursePath,
                        },
                        query: {},
                        state: {},
                      }}
                    >
                      {transaction.item}
                    </RouteLink>
                  </td>
                  <td>
                    <UserRow>
                      <UserIcon src={transaction.user.smallAvatar} />
                      <RouteLink
                        to={{
                          type: ROUTE_USER_PROFILE_PAGE,
                          params: {
                            profileName: transaction.user.profileName,
                          },
                        }}
                      >
                        {transaction.user.fullName}
                      </RouteLink>
                    </UserRow>
                  </td>
                  <td>{`$${transaction.amountReceived} ${transaction.currency}`}</td>
                  <td>{`$${transaction.processingFee} ${transaction.currency}`}</td>
                  <td>{`$${transaction.revenueShare} ${transaction.currency}`}</td>
                  <td>{moment(transaction.date).format('DD/mm/YYYY')}</td>
                  <td>
                    <Chip
                      variant={getChipVariantFromPaymentTransactionStatus(
                        transaction.status
                      )}
                      label={capitalizeFirstLetter(transaction.status)}
                    />
                  </td>
                  <td>
                    <ViewDetailsButton
                      onClick={() => onViewDetailsAction(transaction)}
                    >
                      View details
                    </ViewDetailsButton>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </StyledTable>
        <Paginate
          totalPages={Math.ceil(total / paymentsPageSize)}
          page={paymentsPageNumber}
          pageSize={paymentsPageSize}
          totalResults={total}
          handlePageSizeChange={handlePageSizeChange}
          handlePageChange={handlePageChange}
          pageSizeOptions={[10, 25, 50, 100]}
          showPageSizeSelector
        />
      </div>
      <ModalDialog
        show={showViewDetailsModal}
        onHide={onHideModal}
        dialogClassName="payment-transaction-details-modal"
        title="Payment transaction details"
        actions={[
          {
            label: 'Done',
            onClick: onHideModal,
            type: 'default',
          },
        ]}
      >
        <StyledModalTable>
          <thead>
            <tr>
              <th>Item</th>
              <th>Quantity</th>
              <th>Unit price</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{transactionDetails?.item}</td>
              <td>1</td>
              <td>
                ${transactionDetails?.total} {transactionDetails?.currency}
              </td>
            </tr>
            <tr>
              <td>Tax</td>
              <td>1</td>
              <td>
                {transactionDetails?.tax && transactionDetails?.tax > 0
                  ? '-'
                  : ''}
                ${transactionDetails?.tax} {transactionDetails?.currency}
              </td>
            </tr>
            <tr>
              <td>Revenue share (to OpenLearning)</td>
              <td>1</td>
              <td>
                {transactionDetails?.revenueShare &&
                transactionDetails?.revenueShare > 0
                  ? '-'
                  : ''}
                ${transactionDetails?.revenueShare}{' '}
                {transactionDetails?.currency}
              </td>
            </tr>
            <tr>
              <td>Stripe fees</td>
              <td>1</td>
              <td>
                {transactionDetails?.stripeFee &&
                transactionDetails?.stripeFee > 0
                  ? '-'
                  : ''}
                ${transactionDetails?.stripeFee} {transactionDetails?.currency}
              </td>
            </tr>
            <tr>
              <td colSpan={3}>
                {transactionDetails?.status ===
                UserSubscriptionPlanPaymentTransactionStatus.REFUNDED
                  ? `Total`
                  : `You've made`}{' '}
                $
                {`${transactionDetails?.amountReceived} ${transactionDetails?.currency}`}
              </td>
            </tr>
          </tbody>
        </StyledModalTable>
      </ModalDialog>
    </div>
  );
};

export type CourseMarketplaceProps = {
  subscriptionId: string;
  planType: SubscriptionPlanType;
  accountStatus: UserSubscriptionStripeConnectStatus;
  paymentTransactions: UserSubscriptionPlanPaymentTransactions;
  goToStripeConnect: () => Promise<void>;
  revenueShare: RevenueShare | null;
  onSetupStripeAccountClick: (country: string) => Promise<void>;
  fetchSubscriptionPaymentTransactions: (
    subscriptionId: string,
    afterPaymentId?: string,
    limit?: number,
    page?: number
  ) => Promise<void>;
};

export const CourseMarketplace = ({
  subscriptionId,
  planType,
  accountStatus,
  paymentTransactions,
  goToStripeConnect,
  revenueShare,
  onSetupStripeAccountClick,
  fetchSubscriptionPaymentTransactions,
}: CourseMarketplaceProps): ReactElement => (
  <div>
    <Header>
      <Heading>Course marketplace</Heading>
    </Header>
    {accountStatus === 'no_revenue_share' ||
    accountStatus === 'ol_revenue_no_stripe_account' ||
    (accountStatus === 'no_stripe_account' && !revenueShare?.useOlConnect) ? (
      <CourseMarketplaceSetup
        {...{
          planType,
          accountStatus,
          onSetupStripeAccountClick,
        }}
      />
    ) : revenueShare ? (
      <CourseMarketplaceManager
        {...{
          subscriptionId,
          revenueShare,
          accountStatus,
          paymentTransactions,
          goToStripeConnect,
          fetchSubscriptionPaymentTransactions,
        }}
      />
    ) : null}
  </div>
);
